import { CustomerBooking } from '../../types';

export type SimultaneousGroup = {
    bookings: CustomerBooking[];
    hiddenCount: number;
    corner: 'right' | 'left';
};

export type LimitedBookings = {
    visible: CustomerBooking[];
    groups: { [visibleBookingId: string]: SimultaneousGroup };
};

type Placed = {
    booking: CustomerBooking;
    start: number;
    end: number;
    column: number;
};

function overlaps(first: Placed, second: Placed) {
    return first.start < second.end && second.start < first.end;
}

function placeInColumns(bookings: CustomerBooking[]): Placed[][] {
    let sorted = bookings
        .map((booking) => ({
            booking,
            start: new Date(booking.start).getTime(),
            end: new Date(booking.end).getTime(),
            column: 0
        }))
        .sort((first, second) => first.start - second.start || first.end - second.end);

    let busyTimes: Placed[][] = [];
    let columnEnds: number[] = [];
    let busyUntil = -Infinity;
    for (let placed of sorted) {
        if (placed.start >= busyUntil) {
            busyTimes.push([]);
            columnEnds = [];
        }
        let freeColumn = columnEnds.findIndex((end) => end <= placed.start);
        placed.column = freeColumn === -1 ? columnEnds.length : freeColumn;
        columnEnds[placed.column] = placed.end;
        busyUntil = Math.max(busyUntil, placed.end);
        busyTimes[busyTimes.length - 1].push(placed);
    }
    return busyTimes;
}

export function limitSimultaneousBookings(
    bookings: CustomerBooking[],
    maximumSideBySide: number,
    isInLastColumn: (start: Date) => boolean = () => false
): LimitedBookings {
    let result: LimitedBookings = { visible: [], groups: {} };

    for (let busyTime of placeInColumns(bookings)) {
        let shown = busyTime.filter((placed) => placed.column < maximumSideBySide);
        let firstHidden = busyTime.find((placed) => placed.column >= maximumSideBySide);
        result.visible.push(...shown.map((placed) => placed.booking));

        if (firstHidden) {
            let corner = isInLastColumn(new Date(firstHidden.start)) ? 'left' : 'right';
            let carrierColumn = corner === 'left' ? 0 : maximumSideBySide - 1;
            let carrier = shown.find(
                (placed) => placed.column === carrierColumn && overlaps(placed, firstHidden!)
            )!;
            result.groups[carrier.booking.id] = {
                bookings: busyTime.map((placed) => placed.booking),
                hiddenCount: busyTime.length - shown.length,
                corner
            };
        }
    }
    return result;
}
